import { Image as ImageIcon, Palette, Save, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getBrandLogoUrl, setBrandLogoUrl, useBrandLogoUrl } from "../lib/branding";
import { tenantContext } from "../lib/tenant";

export function BrandingSettingsPage() {
  const { t } = useTranslation();
  const tenant = tenantContext();
  const live = useBrandLogoUrl();

  const [draft, setDraft] = useState<string>(getBrandLogoUrl(tenant.slug) ?? "");
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  useEffect(() => {
    setDraft(getBrandLogoUrl(tenant.slug) ?? "");
  }, [tenant.slug]);

  const save = () => {
    setBrandLogoUrl(tenant.slug, draft);
    setSavedFlash(draft ? t("app.brand_logo_saved") : t("app.brand_logo_cleared"));
    window.setTimeout(() => setSavedFlash(null), 2000);
  };

  const clear = () => {
    setDraft("");
    setBrandLogoUrl(tenant.slug, "");
    setSavedFlash(t("app.brand_logo_cleared"));
    window.setTimeout(() => setSavedFlash(null), 2000);
  };

  const isValidUrl = (() => {
    if (draft.trim() === "") return true;
    try {
      const u = new URL(draft.trim());
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  })();

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
          <Palette className="h-6 w-6 text-slate-400" />
          {t("app.brand_title")}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {t("app.brand_subtitle")}{" "}
          <span className="font-mono">{tenant.slug ?? "default"}</span>.
        </p>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          <ImageIcon className="h-4 w-4" />
          {t("app.brand_section_logo")}
        </h2>

        <label className="block text-sm">
          <span className="font-medium text-slate-700">{t("app.brand_url_label")}</span>
          <input
            type="url"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("app.brand_url_placeholder")}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <span className="mt-1 block text-xs text-slate-500">
            {t("app.brand_url_help")}
          </span>
          {!isValidUrl && (
            <span className="mt-1 block text-xs text-rose-700">
              {t("app.brand_url_invalid")}
            </span>
          )}
        </label>

        <div className="mt-6">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            {t("app.brand_preview_label")}
          </div>
          <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
            {draft.trim() ? (
              <img
                src={draft.trim()}
                alt={t("app.brand_preview_alt")}
                className="h-9 max-h-9 w-auto max-w-[160px] object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.opacity = "0.3";
                }}
              />
            ) : (
              <span className="grid h-8 w-8 place-items-center rounded-md bg-brand-gradient text-white">
                ★
              </span>
            )}
            <span className="text-xs italic text-slate-500">
              {draft.trim()
                ? t("app.brand_preview_yours")
                : t("app.brand_preview_default")}
            </span>
          </div>
        </div>

        {live && live !== draft && (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            {t("app.brand_currently_active")}{" "}
            <span className="break-all font-mono">{live}</span>
          </div>
        )}

        {savedFlash && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {savedFlash}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          {(live || draft) && (
            <button
              type="button"
              onClick={clear}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <Trash2 className="h-4 w-4" /> {t("app.brand_reset_default")}
            </button>
          )}
          <button
            type="button"
            onClick={save}
            disabled={!isValidUrl}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {t("app.brand_save")}
          </button>
        </div>
      </section>
    </div>
  );
}
